import React from 'react';
import { Box, Button, Chip, Typography } from '@mui/material';
import { AutoAwesome } from '@mui/icons-material';
import type { DashboardAnalysis, DashboardTxn } from '../../services/dashboard-service';
import { formatCurrency } from '../../utils/currency';
import { useBaseCurrency } from '../../stores/currency-store';
import { EmptyLine, NavigateFn, SectionCard, num, shortDate } from './shared';

/** Section 5: the newest analysis (a bill breakdown when there is one). */
export const LatestAnalysis: React.FC<{ analysis: DashboardAnalysis | null; onNavigate: NavigateFn }> = ({
  analysis, onNavigate,
}) => {
  if (!analysis) {
    return (
      <SectionCard title="Latest analysis">
        <EmptyLine>No analysis yet. Run one to get a written read of your month: where the money went and what looks off.</EmptyLine>
        <Button variant="outlined" size="small" startIcon={<AutoAwesome />}
          onClick={() => onNavigate({ to: '/analysis/' })}>
          Run an analysis
        </Button>
      </SectionCard>
    );
  }
  const open = { to: '/analysis/$id', params: { id: analysis.id } };
  return (
    <SectionCard title="Latest analysis" onNavigate={onNavigate}
      subtitle={new Date(analysis.created_at).toLocaleDateString('en-CA', { year: 'numeric', month: 'short', day: 'numeric' })}
      action={{ label: 'Open', link: open }}>
      <Typography variant="body2" sx={{ fontWeight: 650, mb: 1 }}>{analysis.title}</Typography>
      {analysis.bullets.length > 0 ? (
        <>
          <Typography variant="overline" sx={{ color: 'var(--text-secondary)', lineHeight: 1.6 }}>
            Notes &amp; anomalies
          </Typography>
          <Box component="ul" sx={{ m: 0, pl: 2.25 }}>
            {analysis.bullets.map((b, i) => (
              <Typography component="li" variant="body2" key={i}
                sx={{ color: 'var(--text-secondary)', mb: 0.75, lineHeight: 1.5 }}>
                {b}
              </Typography>
            ))}
          </Box>
        </>
      ) : (
        analysis.summary && (
          <Typography variant="body2" sx={{ color: 'var(--text-secondary)', lineHeight: 1.5 }}>
            {analysis.summary}
          </Typography>
        )
      )}
    </SectionCard>
  );
};

/** Category chip in the category's own group colour, text kept in ink. */
const CategoryChip: React.FC<{ txn: DashboardTxn }> = ({ txn }) => {
  const uncategorised = txn.category_name.toLowerCase().startsWith('uncategori');
  return (
    <Chip size="small" label={txn.category_name}
      icon={txn.category_color ? (
        <Box component="span" sx={{ width: 8, height: 8, borderRadius: '50%', background: txn.category_color, ml: '8px !important' }} />
      ) : undefined}
      variant="outlined"
      sx={{ height: 22, maxWidth: 160, color: uncategorised ? 'var(--warn-text)' : 'var(--text-secondary)',
        borderColor: 'var(--border-soft)' }} />
  );
};

/**
 * Section 7: the ten newest transactions across every account, read from the
 * transactions themselves -- the old card read snapshots, which no importer
 * writes, and so said "No recent activity" over a thousand transactions.
 */
export const RecentTransactions: React.FC<{ txns: DashboardTxn[]; onNavigate: NavigateFn }> = ({
  txns, onNavigate,
}) => {
  const base = useBaseCurrency();
  return (
    <SectionCard title="Recent activity" onNavigate={onNavigate}
      action={{ label: 'All transactions', link: { to: '/transactions' } }}>
      {txns.length === 0 ? (
        <EmptyLine>No transactions yet. Connect a bank or import a statement to see activity here.</EmptyLine>
      ) : (
        <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0 }} aria-label="Recent transactions">
          {txns.map((t) => {
            const amt = num(t.amount);
            return (
              <Box component="li" key={t.id}
                sx={{ display: 'grid', gridTemplateColumns: { xs: '52px 1fr auto', sm: '60px 1fr auto auto' },
                  alignItems: 'center', columnGap: 1.5, py: 0.9, borderTop: '1px solid var(--border-soft)' }}>
                <Typography variant="caption" className="fw-num" sx={{ color: 'var(--text-secondary)' }}>
                  {shortDate(t.date)}
                </Typography>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {t.description || '—'}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'var(--text-faint)', whiteSpace: 'nowrap', overflow: 'hidden',
                    textOverflow: 'ellipsis', display: 'block' }}>
                    {t.account_name}
                  </Typography>
                </Box>
                <Box sx={{ display: { xs: 'none', sm: 'block' } }}><CategoryChip txn={t} /></Box>
                <Typography variant="body2" className="fw-num"
                  sx={{ fontWeight: 600, textAlign: 'right', color: amt > 0 ? 'var(--inflow-text)' : 'var(--text)' }}>
                  {formatCurrency(t.amount, t.currency || base, { base })}
                </Typography>
              </Box>
            );
          })}
        </Box>
      )}
    </SectionCard>
  );
};
